/**
 * Image processing utilities for image upload and cropping
 * Handles image validation, cropping, and base64 conversion
 * Supports multiple presets for different use cases (logos, avatars, etc.)
 */

export interface Area {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface ImageValidationResult {
  isValid: boolean;
  error?: string;
}

export interface ImageDimensions {
  width: number;
  height: number;
}

export interface ImagePreset {
  sizePx: number;
  maxInputSizeMB: number;
  maxOutputSizeKB: number;
  jpegQuality: number;
  aspectRatio: number;
}

// Default validation constants
const VALID_IMAGE_FORMATS = ['image/jpeg', 'image/jpg', 'image/png'];
const MAX_INPUT_FILE_SIZE_MB = 5;

// Image presets for different use cases
export const IMAGE_PRESETS = {
  LOGO: {
    sizePx: 200,
    maxInputSizeMB: 5,
    maxOutputSizeKB: 500,
    jpegQuality: 0.9,
    aspectRatio: 1, // 1:1 square
  },
} as const;

// Legacy constants for backward compatibility
const LOGO_SIZE_PX = IMAGE_PRESETS.LOGO.sizePx;
const MAX_OUTPUT_FILE_SIZE_KB = IMAGE_PRESETS.LOGO.maxOutputSizeKB;
const JPEG_QUALITY = IMAGE_PRESETS.LOGO.jpegQuality;

/**
 * Validates if a file meets image requirements
 * @param file - The file to validate
 * @param maxSizeMB - Maximum file size in MB (default: 5MB)
 * @returns Validation result with error message if invalid
 */
export function validateImageFile(
  file: File,
  maxSizeMB: number = MAX_INPUT_FILE_SIZE_MB,
): ImageValidationResult {
  // Check format
  if (!VALID_IMAGE_FORMATS.includes(file.type)) {
    return {
      isValid: false,
      error: 'Formato no válido. Use .jpg, .jpeg o .png',
    };
  }

  // Check size
  const fileSizeMB = file.size / (1024 * 1024);
  if (fileSizeMB > maxSizeMB) {
    return {
      isValid: false,
      error: `El archivo excede el tamaño máximo de ${maxSizeMB}MB (actual: ${fileSizeMB.toFixed(2)}MB)`,
    };
  }

  return { isValid: true };
}

/**
 * Legacy function name for backward compatibility
 * @deprecated Use validateImageFile instead
 */
export function validateLogoFile(file: File): ImageValidationResult {
  return validateImageFile(file, IMAGE_PRESETS.LOGO.maxInputSizeMB);
}

/**
 * Creates an HTMLImageElement from a URL
 * @param url - The image URL (data URL or external URL)
 * @returns Promise that resolves with the loaded image
 */
export function createImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new window.Image();
    image.addEventListener('load', () => resolve(image));
    image.addEventListener('error', (error) => reject(new Error(error.message || 'Error loading image')));
    image.setAttribute('crossOrigin', 'anonymous');
    image.src = url;
  });
}

/**
 * Gets the dimensions of an image from its data URL
 * @param dataUrl - The image data URL
 * @returns Promise that resolves with width and height
 */
export async function getImageDimensions(dataUrl: string): Promise<ImageDimensions> {
  const image = await createImage(dataUrl);
  return {
    width: image.width,
    height: image.height,
  };
}

/**
 * Converts a File to a data URL
 * @param file - The file to convert
 * @returns Promise that resolves with the data URL
 */
export function fileToDataURL(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.addEventListener('load', () => resolve(reader.result as string));
    reader.addEventListener('error', reject);
    reader.readAsDataURL(file);
  });
}

/**
 * Crops an image to the specified dimensions and returns base64
 * @param imageSrc - The source image URL
 * @param pixelCrop - The crop area in pixels
 * @param outputWidth - Target width (default: 200px)
 * @param outputHeight - Target height (default: 200px)
 * @returns Promise that resolves with base64 encoded image
 */
export async function getCroppedImageBase64(
  imageSrc: string,
  pixelCrop: Area,
  outputWidth: number = LOGO_SIZE_PX,
  outputHeight: number = LOGO_SIZE_PX,
): Promise<string> {
  const image = await createImage(imageSrc);
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');

  if (!context) {
    throw new Error('Failed to get canvas context');
  }

  // Set canvas size to target dimensions
  canvas.width = outputWidth;
  canvas.height = outputHeight;

  // Draw the cropped image onto the canvas
  context.drawImage(
    image,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    outputWidth,
    outputHeight,
  );

  // Convert to base64 with fixed quality
  return canvas.toDataURL('image/jpeg', JPEG_QUALITY);
}

/**
 * Extracts the base64 data from a data URL (removes the prefix)
 * @param dataUrl - The data URL to process
 * @returns Base64 string without the data:image/...;base64, prefix
 */
export function extractBase64FromDataURL(dataUrl: string): string {
  const matches = dataUrl.match(/^data:image\/\w+;base64,(.+)$/);
  if (!matches || matches.length < 2) {
    throw new Error('Invalid data URL format');
  }
  return matches[1];
}

/**
 * Gets the file size in KB from a base64 string
 * @param base64String - The base64 encoded string
 * @returns Size in kilobytes
 */
export function getBase64SizeKB(base64String: string): number {
  const stringLength = base64String.length;
  const sizeInBytes = (stringLength * 3) / 4;
  return sizeInBytes / 1024;
}

/**
 * Validates that the cropped image base64 meets size requirements
 * @param base64String - The base64 string to validate
 * @param maxSizeKB - Maximum size in KB (default: 500KB for logos)
 * @returns Validation result
 */
export function validateCroppedImageSize(
  base64String: string,
  maxSizeKB: number = MAX_OUTPUT_FILE_SIZE_KB,
): ImageValidationResult {
  const sizeKB = getBase64SizeKB(base64String);
  
  if (sizeKB > maxSizeKB) {
    return {
      isValid: false,
      error: `La imagen procesada excede ${maxSizeKB}KB (${Math.round(sizeKB)}KB). Intente con una imagen de menor resolución o con menos detalles.`,
    };
  }

  return { isValid: true };
}

/**
 * Constants exported for use in components
 */
export const IMAGE_CONSTANTS = {
  VALID_FORMATS: VALID_IMAGE_FORMATS,
  MAX_INPUT_SIZE_MB: MAX_INPUT_FILE_SIZE_MB,
  MAX_OUTPUT_SIZE_KB: MAX_OUTPUT_FILE_SIZE_KB,
  LOGO_SIZE_PX,
  JPEG_QUALITY,
  ACCEPTED_FILE_TYPES: '.jpg,.jpeg,.png',
  PRESETS: IMAGE_PRESETS,
} as const;
